import { differenceInYears } from 'date-fns';
import { Gender } from '@aldebaran/prisma';
import type { PatientDataCollectionConfig } from '@aldebaran/common/util/patientDataCollection';

type PatientInfo = {
  firstName: string | null;
  lastName: string | null;
  dateOfBirth: Date | null;
  sex: Gender | null;
  birthName?: string | null;
};

export const isUnder16 = (dateOfBirth: Date): boolean => {
  return differenceInYears(new Date(), new Date(dateOfBirth)) < 16;
};

type ComputeSelfArgs = {
  selfURLParam: string | undefined;
  interlocuter: 'SELF' | 'OTHER' | undefined;
  dateOfBirth: Date | null;
  assistedByPro?: boolean;
};

export const computeSelf = ({
  selfURLParam,
  interlocuter,
  dateOfBirth,
  assistedByPro,
}: ComputeSelfArgs): boolean => {
  if (dateOfBirth && isUnder16(dateOfBirth)) return false;
  if (assistedByPro) return false;
  if (selfURLParam === 'false') return false;
  if (selfURLParam === 'true') return true;
  if (interlocuter !== undefined) return interlocuter !== 'OTHER';
  return true;
};

export const isPatientInformationComplete = (
  patient: PatientInfo,
  {
    requireDateOfBirthAndSex = true,
    patientDataCollectionConfig,
  }: {
    requireDateOfBirthAndSex?: boolean;
    patientDataCollectionConfig?: PatientDataCollectionConfig;
  } = {},
) => {
  if (!patient) return false;
  const hasName = !!patient.firstName?.trim() && !!patient.lastName?.trim();
  if (!hasName) return false;
  if (
    patientDataCollectionConfig?.BIRTH_NAME.required &&
    !patient.birthName?.trim()
  )
    return false;

  if (!requireDateOfBirthAndSex) return true;
  return !!(patient.dateOfBirth && patient.sex?.trim());
};

export const isEligibleForMonBilanPrevention = (
  dateOfBirth: Date | null,
): boolean => {
  if (!dateOfBirth) return false;

  const age = differenceInYears(new Date(), new Date(dateOfBirth));

  const isInRange = (min: number, max: number) => age >= min && age <= max;

  return (
    isInRange(18, 25) ||
    isInRange(45, 50) ||
    isInRange(60, 65) ||
    isInRange(70, 75)
  );
};
